import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  HomeIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  Cog6ToothIcon,
  UserIcon,
  ArrowLeftStartOnRectangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
  ArrowTrendingUpIcon,
  BookOpenIcon,
  CodeBracketIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import useMediaQuery from '../hooks/useMediaQuery';
import logoDark from '../assets/logo.svg';
import logoLight from '../assets/logo-light.svg';

interface NavItem {
  path: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { logout } = useAuth();
  const { currentTenant } = useTenant();
  const { theme } = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const mainNavItems: NavItem[] = [
    { path: '/dashboard', icon: HomeIcon, label: 'Dashboard' },
    { path: '/skill-diagnostic', icon: ChartBarIcon, label: 'Skill Diagnostic' },
    { path: '/practice', icon: ChatBubbleLeftRightIcon, label: 'Practice with AI' },
    { path: '/learning-resources', icon: BookOpenIcon, label: 'Learning Resources' },
    { path: '/corporate-vocabulary', icon: AcademicCapIcon, label: 'Corporate Vocabulary' },
    { path: '/coding', icon: CodeBracketIcon, label: 'Coding Problems' },
    { path: '/progress', icon: ArrowTrendingUpIcon, label: 'Progress' },
    { path: '/hr-bot', icon: UserIcon, label: 'HR Bot' },
  ];

  const bottomNavItems: NavItem[] = [
    { path: '/settings', icon: Cog6ToothIcon, label: 'Settings' },
    { path: '/profile', icon: UserIcon, label: 'Profile' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const renderNavItem = (item: NavItem) => {
    const tenantPath = currentTenant ? `/${currentTenant.website_url}${item.path}` : item.path;
    const isActive = location.pathname === tenantPath;
    return (
      <Link
        key={item.path}
        to={tenantPath}
        className={`flex items-center ${isCollapsed ? 'justify-center' : 'px-3'} py-2.5 text-sm font-medium rounded-md transition-colors duration-200 ${
          isActive
            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-200'
            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
        }`}
        onClick={isMobile ? onClose : undefined}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" />
        {!isCollapsed && (
          <span className="ml-3 transition-opacity duration-300">
            {item.label}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div
      className={`relative flex flex-col h-full transition-all duration-300 ease-in-out bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 ${
        isCollapsed ? 'w-16' : 'w-64'
      }`}
    >
      <div className="flex-1 overflow-y-auto py-4">
        <div className={`px-3 mb-6 flex items-center justify-between`}>
          <div className="flex items-center">
            <img
              src={theme === 'dark' ? logoDark : logoLight}
              alt="Memriti Logo"
              className="h-8 w-auto"
            />
          </div>
          {isMobile ? (
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          ) : (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
            >
              {isCollapsed ? (
                <ChevronRightIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              ) : (
                <ChevronLeftIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              )}
            </button>
          )}
        </div>

        <nav className="space-y-1 px-2">
          {mainNavItems.map(renderNavItem)}
        </nav>
      </div>

      <div className="px-3 py-4 border-t border-gray-200 dark:border-gray-700 space-y-1">
        {bottomNavItems.map(renderNavItem)}

        <button
          onClick={handleLogout}
          className={`flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors duration-200 w-full`}
        >
          <ArrowLeftStartOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
          {!isCollapsed && (
            <span className="ml-3">
              Logout
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;